import { toast } from 'react-toastify';
import { urls } from 'services/_url';
import { useNavigate } from 'react-router-dom';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import {
  loadCampaignsToStore,
  addOneCampaignToStore,
  removeOneCampaignFromStore
} from 'store/actions/campaign';
import { setPageTitle } from 'store/actions/header';
import { Button } from 'app/shared/button';
import { campaignCanBeCancelled, campaignCanBeEdited } from 'utilities/form';
import GmModal from 'app/shared/modal/modal';
import { OptionsModal } from 'app/shared/option-modal';
import styles from '../campaign.module.css';
import { PageContentWrapper } from 'app/layouts/wrapper/page-content';
import { GridColumn, GridRow } from 'app/layouts/grid';
import { Spacer } from 'app/layouts/generic';
import { SegmentedTabs } from 'app/shared/segmented-tabs';
import { CampaignStatBar } from './statbar';
import { useCampaignService } from 'hooks/mailing/campaign';
import useScreenSize from 'hooks/size';
import { truncateText } from 'app/shared/utils/general';
import { useDraftService } from 'hooks/mailing/draft';
import { PermissionsContext } from 'contexts/permissions';
import { useMailingConfigService } from 'hooks/mailing/configuration';
import { useSubscriptionService } from 'hooks/sales/subscription';
import { InfoBanner } from 'app/shared/info-banner';

import { useEventsService } from 'hooks/automation/event';
import { filter_config } from './config/filter';
import { useMailboxService } from 'hooks/users/mailbox';
import { Badge } from 'app/shared/badge';

// Live/test is a tab rather than a filter; each value is the bool= query it sends.
const campaign_mode_tabs = [
  { value: '-is_test_campaign', label: 'Live' },
  { value: 'is_test_campaign', label: 'Test' }
];

const ListCampaigns = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const { balance: wallet_balance } = useSelector((state) => state.wallet);

  const { fetchCampaign, fetchCampaigns, searchCampaigns, updateCampaign } = useCampaignService();
  const { createDraft, deleteDraft } = useDraftService();
  const { fetchEvents } = useEventsService();
  const { fetchMailboxes } = useMailboxService();
  const { fetchConfiguration } = useMailingConfigService();
  const { fetchSubscription } = useSubscriptionService();
  const { permissions } = useContext(PermissionsContext);
  const { tenant_id, token, id: user_id } = useSelector((state) => state.user_data);
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const campaigns_in_store = useSelector((state) => state.campaigns);
  const { isMobile, isTablet } = useScreenSize();

  const [campaigns, setCampaigns] = useState([]);
  const [events, setEvents] = useState([]);
  const [has_exceeded_limit, setHasExceededLimit] = useState(false);
  const [is_dkim_set, setIsDKIMSet] = useState(false);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mailboxes, setMailboxes] = useState([]);
  const [show_cancel_confirmation, setShowCancelConfirmation] = useState(false);
  const [show_creation_options, setShowCreationOptions] = useState(false);
  const [show_delete_confirmation, setShowDeleteConfirmation] = useState(false);
  const [campaign_to_delete, setCampaignToDelete] = useState(0);
  const [campaign_to_cancel, setCampaignToCancel] = useState({});
  const [total_campaigns, setTotalCampaigns] = useState(0);
  const [campaign_mode, setCampaignMode] = useState('-is_test_campaign');
  const { creationOptions } = useCampaignService();

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'All Campaigns', path: '.' }]));
    fetchConfiguration().then(({ configurations: [config] }) => {
      if (!config) return;

      const { dns } = config;
      setIsDKIMSet(() => dns && !!dns.length);
    });

    fetchSubscription({ query_string: 'plan_code=campaign&status=active' }).then(
      ({ subscriptions: [subscription] }) => {
        if (!subscription) return;
        const {
          resources: { usage, limit }
        } = subscription;
        setHasExceededLimit(() => limit !== 0 && usage >= limit);
      }
    );

    fetchEvents({ query_string: 'type=user' }).then(({ events }) => {
      setEvents(() => events.map((e) => ({ ...e, value: e.code.split(':')[0], label: e.name })));
    });

    fetchMailboxes().then(({ mailboxes: [mailbox] }) => {
      const emails = mailbox ? mailbox.emails : [];
      setMailboxes(emails.map((email) => ({ value: email, label: email })));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const parsed_campaigns = Object.values(campaigns_in_store);
    setCampaigns(() => parsed_campaigns);
  }, [campaigns_in_store]);

  const config = {
    actions: {
      single: (campaign) => [
        [
          {
            value: 'view',
            label: 'View'
          },
          {
            value: 'edit',
            label: 'Edit',
            hidden: !campaignCanBeEdited(campaign) || !permissions['campaign:update']
          },
          { label: 'Clone', value: 'duplicate' }
          // { label: 'Resend to non-openers', value: 'retry', hidden: campaign.status !== 'processed' },
        ],
        [
          {
            color: 'red',
            label: 'Cancel',
            value: 'cancel',
            hidden: campaign.status !== 'queued'
          },
          {
            color: 'red',
            label: 'Delete',
            value: 'delete',
            hidden: campaign.status !== 'draft' || !permissions['campaign:delete']
          }
        ]
      ]
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Campaign Name',
        key: 'name',
        sortable: true,
        isTitle: true,
        formatter: (value) => truncateText(value, 25) || ''
      },
      {
        title: 'Sender Name',
        key: 'sender_name',
        sortable: true,
        isTagline: true,
        formatter: (value) => value || 'N/A'
      },
      {
        title: 'Sender Email',
        key: 'sender_email',
        sortable: true,
        isTagline: true,
        formatter: (value) => {
          return truncateText(value, isTablet ? 25 : 35) || 'N/A';
        }
      },
      {
        title: 'Status',
        sortable: true,
        key: 'status',
        formatter: (value) => {
          switch (value) {
            case 'processed':
              return <Badge type="green" text={value} />;
            case 'processing':
            case 'queued':
              return <Badge type="blue" text={value} />;
            case 'cancelled':
              return <Badge type="red" text={value} />;
            default:
              return <Badge type="red" text={value} />;
          }
        },
        isBadge: true
      },
      {
        title: 'Send date',
        key: 'schedule',
        sortable: true,
        formatter: (value) => {
          if (!value?.date) return '...';
          return new Date(value.date).toDateString();
        },
        isMetadata: true
      }
    ],
    is_search_mode,
    items: campaigns,
    num_of_rows: 10,
    search_key: 'name',
    search_text: ''
  };

  const handleDeleteDraft = async (id) => {
    const result = await deleteDraft(id);
    if (!result) {
      toast.error('Could not delete draft.');
      return;
    }

    dispatch(removeOneCampaignFromStore(id));
    toast.success('Draft deleted successfully.');
  };

  const handleDeleteConfirmation = (permitted) => {
    if (permitted) {
      handleDeleteDraft(campaign_to_delete);
    }

    setCampaignToDelete(0);
    setShowDeleteConfirmation(false);
  };

  const cancelQueued = async (campaign) => {
    const { id } = campaign;
    const result = await updateCampaign(id, {
      data: { ...campaign, old_status: campaign.status, status: 'cancelled' }
    });
    if (!result) return;
    dispatch(addOneCampaignToStore({ ...campaign, status: 'cancelled' }));
  };

  const duplicate = async (campaign) => {
    toast.info(`cloning campaign: ${campaign.name}`);
    const { campaign: full_campaign } = await fetchCampaign(campaign.id);

    if (!full_campaign || !full_campaign.id) {
      return toast.error('Cloning failed. Please try again');
    }

    const new_data = {
      ...full_campaign,
      cost: '0.00',
      failed_sends_list: [],
      schedule: { ...full_campaign.schedule },
      name: `[DUPLICATE] ${full_campaign.name}`,
      status: 'draft',
      attachments: []
    };

    delete new_data.id;
    delete new_data._id;
    delete new_data.time_stamp;
    delete new_data.is_enqueued;
    delete new_data.created_on;
    delete new_data.updated_on;
    delete new_data.failed_sends_list;
    delete new_data.metadata;
    delete new_data.schedule.date;
    delete new_data.is_test_campaign;

    const draft = await createDraft({ data: new_data });
    if (!draft) {
      toast.error('Cloning failed.');
      return;
    }

    dispatch(addOneCampaignToStore(draft));
    toast.success('Cloning successful.');
  };

  const handleCancelConfirmation = (permitted) => {
    if (permitted) {
      cancelQueued(campaign_to_cancel);
    }

    setCampaignToCancel({});
    setShowCancelConfirmation(false);
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        case 'edit':
          if (!campaignCanBeEdited(data)) {
            toast.warning('Cannot edit campaign.');
            break;
          }

          if (data.is_advanced) {
            window.location.href = `${urls.editor}/campaigns/${data.id}?t=${token}&tenant_id=${tenant_id}&user_id=${user_id}`;
            return;
          }

          history(`/editor/campaign/${data.id}`);
          break;
        case 'duplicate':
          duplicate(data);
          break;
        case 'delete':
          if (data.status !== 'draft') {
            toast.info(`Cannot delete ${data.status} campaigns.`);
            break;
          }

          setCampaignToDelete(data.id);
          setShowDeleteConfirmation(true);
          break;
        case 'cancel':
          if (data.status !== 'queued') {
            toast.info(`Cannot cancel ${data.status} campaigns.`);
            break;
          }

          if (!campaignCanBeCancelled(data.schedule.date)) {
            toast.info('Cannot cancel this campaign.');
            break;
          }
          setCampaignToCancel(data);
          setShowCancelConfirmation(true);
          break;
        case 'retry':
          handleRetryCampaign(data);
          break;
        default:
          history(`/campaigns/${data.id}`);
      }
    }
  };

  const handleRetryCampaign = async (data) => {
    const { id } = data;
    toast.info('Resending Campaign...');
    const response = await updateCampaign(id, {
      data: { ...data, status: 'queued', is_enqueued: false }
    });
    if (!response) {
      toast.error('Unable to resend campaign.');
      return;
    }
    dispatch(addOneCampaignToStore({ ...data, status: 'queued' }));
    toast.success('Campaign queued successfully');
  };

  const handleItemClick = (payload) => {
    const { id } = payload;
    history(`/campaigns/${id}`);
  };

  const handleDataRequest = async (
    page,
    population = 10,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    try {
      setLoading(true);
      const filter_query =
        !options.filter_query || options.filter_query === '&' ? '' : options.filter_query;
      const query = `bool=${campaign_mode}&${filter_query}`;
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { campaigns, size } = await fetchCampaigns({
        query_string: `${query}&page=${page}&population=${population}&${sort_query}&return_only=is_advanced,id,name,sender_name,sender_email,status,schedule,failed_sends_list,created_on,is_test_campaign`
      });

      setTotalCampaigns(size);
      // Server-paginated table: every page replaces the current rows.
      dispatch(loadCampaignsToStore(campaigns));
    } catch (e) {
      dispatch(loadCampaignsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (
    keys,
    keyword,
    page,
    population = 10,
    options = { filter_query: '&', sort_query: '' }
  ) => {
    if (!keys) return;
    try {
      setLoading(true);
      const filter_query =
        !options.filter_query || options.filter_query === '&' ? '' : options.filter_query;
      const query = `bool=${campaign_mode}&${filter_query}`;
      const sort_query = options.sort_query || 'sort_by=-created_on';

      const { campaigns, size } = await searchCampaigns(keys, keyword, {
        query_string: `${query}&page=${page}&population=${population}&${sort_query}&return_only=id,name,sender_name,sender_email,status,schedule,failed_sends_list,created_on,is_test_campaign`
      });

      setTotalCampaigns(size);
      // Replace (not append) so paginated search results show one page at a time.
      dispatch(loadCampaignsToStore(campaigns));
    } catch (e) {
      dispatch(loadCampaignsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleCampaignModeChange = (mode) => {
    dispatch(loadCampaignsToStore([]));
    setCampaignMode(mode);
  };

  const campaign_mode_toggle = (
    <SegmentedTabs
      options={campaign_mode_tabs}
      value={campaign_mode}
      onChange={handleCampaignModeChange}
      label="Campaign mode"
    />
  );

  const table_actions = (
    <>
      <Button
        icon_name="add"
        text="Create campaign"
        disabled={!permissions['campaign:create'] || (has_exceeded_limit && wallet_balance <= 0)}
        onClick={() => setShowCreationOptions(true)}
      />
    </>
  );

  return (
    <PageContentWrapper>
      {!isMobile && (
        <>
          <CampaignStatBar />
          <Spacer multiple={4} />
        </>
      )}
      <GridRow>
        <GridColumn span={4}>
          {is_mobile_view ? (
            <>
              {campaign_mode_toggle}
              <Spacer multiple={2} />
              <MobileDatatable
                key={campaign_mode}
                config={config}
                action={handleDatatableAction}
                onClick={handleItemClick}
                onListModeChange={setSearchMode}
                onDataRequest={handleDataRequest}
                onSearchRequest={handleSearchRequest}
                showHeader
              />
            </>
          ) : (
            <>
              {has_exceeded_limit && (
                <>
                  <InfoBanner
                    type="error"
                    message="You have exceeded your subscription limits for this month."
                  />
                  <Spacer multiple={4} />
                </>
              )}
              <WebDatatable
                config={{
                  ...config,
                  total_count: total_campaigns
                }}
                action={handleDatatableAction}
                onClick={handleItemClick}
                checkbox
                has_filter={true}
                filter_config={filter_config(events, mailboxes)}
                header_tabs={campaign_mode_toggle}
                reset_key={campaign_mode}
                loading_data={loading}
                table_actions={table_actions}
                onDataRequest={handleDataRequest}
                onSearchRequest={handleSearchRequest}
              />
            </>
          )}
          <ConfirmationDialog
            type="error"
            icon_name="trash"
            action_text="Yes, delete"
            v
            title="Delete Campaign"
            message="Are you sure you want to delete this campaign? This action cannot be undone."
            callback={handleDeleteConfirmation}
            is_open={show_delete_confirmation}
          />
          <ConfirmationDialog
            icon_name="warning"
            type="warning"
            action_text="Yes, cancel"
            title="Cancel Campaign"
            message="Are you sure you want to cancel this campaign?"
            callback={handleCancelConfirmation}
            is_open={show_cancel_confirmation}
          />
          <GmModal
            title="Choose creation mode"
            show_modal={show_creation_options}
            onClose={() => setShowCreationOptions(false)}
            bodyClassName={styles.optionsModal}
            show_title
          >
            <OptionsModal options={creationOptions} is_dkim_set={is_dkim_set} />
          </GmModal>
        </GridColumn>
      </GridRow>
    </PageContentWrapper>
  );
};

export default ListCampaigns;
